import { useEffect, useState } from 'react';
import { api, type ApiError } from '../lib/api';
import { useAction } from '../lib/hooks';
import { dateTime } from '../lib/format';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  InfoTooltip,
  Input,
  Loading,
  Modal,
  Table,
  Td,
  Textarea,
  Th,
} from './ui';

interface ApolloSearchParams {
  company_domain?: string;
  person_titles?: string[];
  person_seniorities?: string[];
  org_industries?: string[];
  org_employee_count_range?: {
    min?: number;
    max?: number;
  };
  contact_email_status?: string[];
  per_page?: number;
  page?: number;
}

interface ApolloListImportConfig extends ApolloSearchParams {
  list_ids?: string[];
}

interface ApolloListImport {
  id: string;
  name: string;
  description: string;
  search_params: ApolloListImportConfig;
  sync_interval_hours: number;
  enabled: boolean;
  total_contacts_found: number;
  total_contacts_imported: number;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ApolloContact {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  linkedin_url?: string;
  organization?: {
    name: string;
    industry?: string;
    estimated_num_employees?: number;
  };
}

interface SearchPreview {
  contacts: ApolloContact[];
  breadcrumbs?: {
    page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
}

interface ApolloListSummary {
  id: string;
  name: string;
  count?: number;
}

const TITLE_SUGGESTIONS = [
  'CTO', 'VP Engineering', 'Head of Engineering', 'Director of Engineering',
  'VP Operations', 'Head of Operations', 'Director of Operations',
  'VP Sales', 'Head of Sales', 'Sales Director',
  'VP Marketing', 'Head of Marketing', 'Marketing Director',
  'CEO', 'COO', 'CFO', 'CMO', 'CRO',
  'Head of IT', 'IT Director', 'Chief Architect',
];

const SENIORITY_SUGGESTIONS = [
  { value: 'c_suite', label: 'C-Suite' },
  { value: 'vp', label: 'VP' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
];

const EMAIL_STATUS_OPTIONS = [
  { value: 'verified', label: 'Verified email' },
  { value: 'professional', label: 'Professional email' },
  { value: 'personal', label: 'Personal email' },
];

export function ApolloImportManager() {
  const [imports, setImports] = useState<ApolloListImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingImport, setEditingImport] = useState<ApolloListImport | null>(null);

  const loadImports = useAction(async () => {
    try {
      setLoading(true);
      const data = await api.get<{ imports: ApolloListImport[] }>('/api/apollo/imports');
      setImports(data.imports);
      setError(null);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    void loadImports.run();
    // Load once on mount; loadImports is stable enough for this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-lg font-semibold">
          Apollo List Imports
          <InfoTooltip content="Apollo is a data source only — this imports contacts in, it never sends through Apollo. All outreach still goes through this app's approval and suppression checks." />
        </h2>
        <Button onClick={() => setShowSearch(true)}>New Import</Button>
      </div>

      {error && <ErrorNote message={error} />}

      {imports.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-600">
            No Apollo list imports configured yet. Create one to automatically import contacts
            from a saved Apollo list.
          </p>
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Source</Th>
              <Th tip="How often this search re-runs and imports new matches. 0 means it only runs when you trigger it manually.">
                Sync Interval
              </Th>
              <Th>Last Synced</Th>
              <Th>Imported</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {imports.map((imp) => (
              <tr key={imp.id}>
                <Td className="font-medium">{imp.name}</Td>
                <Td>
                  {imp.search_params?.list_ids?.length
                    ? 'Apollo list'
                    : 'Filter search'}
                </Td>
                <Td>
                  {imp.sync_interval_hours === 0
                    ? 'Manual only'
                    : `Every ${imp.sync_interval_hours}h`}
                </Td>
                <Td>
                  {imp.last_synced_at ? dateTime(imp.last_synced_at) : 'Never'}
                </Td>
                <Td>{imp.total_contacts_imported}</Td>
                <Td>
                  {imp.enabled ? (
                    <span className="text-xs font-medium text-green-700">Active</span>
                  ) : (
                    <span className="text-xs font-medium text-ink-500">Disabled</span>
                  )}
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditingImport(imp);
                        setShowEdit(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          try {
                            await api.post(`/api/apollo/imports/${imp.id}/sync`, {});
                            await loadImports.run();
                          } catch (err) {
                            alert((err as ApiError).message);
                          }
                        })();
                      }}
                    >
                      Sync Now
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {showSearch && (
        <ApolloSearchModal
          onClose={() => setShowSearch(false)}
          onSaved={() => {
            setShowSearch(false);
            void loadImports.run();
          }}
        />
      )}

      {showEdit && editingImport && (
        <ApolloEditImportModal
          import={editingImport}
          onClose={() => {
            setShowEdit(false);
            setEditingImport(null);
          }}
          onSaved={() => {
            setShowEdit(false);
            setEditingImport(null);
            void loadImports.run();
          }}
        />
      )}
    </div>
  );
}

function ApolloSearchModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  // Filter search is switched off (see ApolloFilterSearchUnavailableError on the
  // server), so a new import starts from a saved list.
  const [mode, setMode] = useState<'search' | 'list'>('list');
  const [params, setParams] = useState<ApolloSearchParams>({
    contact_email_status: ['verified'],
    per_page: 100,
  });
  const [lists, setLists] = useState<ApolloListSummary[] | null>(null);
  const [listsError, setListsError] = useState<string | null>(null);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<SearchPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [savingAsImport, setSavingAsImport] = useState(false);
  const [importName, setImportName] = useState('');
  const [importDescription, setImportDescription] = useState('');
  const [syncInterval, setSyncInterval] = useState(0);

  useEffect(() => {
    if (mode !== 'list' || lists !== null) return;
    void (async () => {
      try {
        const data = await api.get<{ lists: ApolloListSummary[] }>('/api/apollo/lists');
        setLists(data.lists);
      } catch (err) {
        setListsError((err as ApiError).message);
      }
    })();
  }, [mode, lists]);

  // What to send to /api/apollo/search and /api/apollo/import: either the
  // filter params, or the ids of the Apollo lists the user picked.
  const requestBody = mode === 'list' ? { list_ids: selectedListIds } : params;

  const doSearch = useAction(async () => {
    if (mode === 'list' && selectedListIds.length === 0) {
      alert('Select at least one Apollo list');
      return;
    }
    try {
      const result = await api.post<SearchPreview>('/api/apollo/search', requestBody);
      setPreview(result);
      setShowPreview(true);
    } catch (err) {
      alert((err as ApiError).message);
    }
  });

  const doImport = useAction(async () => {
    if (mode === 'list' && selectedListIds.length === 0) {
      alert('Select at least one Apollo list');
      return;
    }
    try {
      await api.post('/api/apollo/import', requestBody);
      alert('Import successful!');
      onSaved();
    } catch (err) {
      alert((err as ApiError).message);
    }
  });

  const saveAsConfig = useAction(async () => {
    if (!importName.trim()) {
      alert('Please enter a name for this import');
      return;
    }
    if (mode === 'list' && selectedListIds.length === 0) {
      alert('Select at least one Apollo list');
      return;
    }
    try {
      await api.post('/api/apollo/imports', {
        name: importName,
        description: importDescription,
        ...(mode === 'list'
          ? { list_ids: selectedListIds }
          : { search_params: params }),
        sync_interval_hours: syncInterval,
      });
      alert('Import config saved!');
      onSaved();
    } catch (err) {
      alert((err as ApiError).message);
    }
  });

  return (
    <Modal open title="New Apollo Import" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'search' ? 'primary' : 'secondary'}
            disabled
            title="Apollo no longer serves the endpoint filter search used, and its replacement returns no emails or domains. Build the list in Apollo and import it from there."
          >
            Filter search (unavailable)
          </Button>
          <Button
            size="sm"
            variant={mode === 'list' ? 'primary' : 'secondary'}
            onClick={() => setMode('list')}
          >
            From an Apollo list
          </Button>
        </div>

        {mode === 'list' ? (
          <div className="space-y-2">
            <p className="text-sm text-ink-600">
              Pick one or more lists you've already built in Apollo. Their current members
              are recorded as Apollo signals and linked to prospects already in the app; no
              new prospects are created.
            </p>
            {listsError && <ErrorNote message={listsError} />}
            {!listsError && lists === null && <Loading />}
            {lists !== null && lists.length === 0 && !listsError && (
              <p className="text-sm text-ink-500">No Apollo lists found on this account.</p>
            )}
            {lists && lists.length > 0 && (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded border border-ink-200 p-2">
                {lists.map((list) => (
                  <label key={list.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="rounded border-ink-300"
                        checked={selectedListIds.includes(list.id)}
                        onChange={(e) => {
                          setSelectedListIds(
                            e.target.checked
                              ? [...selectedListIds, list.id]
                              : selectedListIds.filter((id) => id !== list.id),
                          );
                        }}
                      />
                      {list.name}
                    </span>
                    {typeof list.count === 'number' && (
                      <span className="text-ink-500">{list.count}</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email Domain" hint="e.g., acme.com">
                <Input
                  type="text"
                  value={params.company_domain || ''}
                  onChange={(e) =>
                    setParams({ ...params, company_domain: e.target.value || undefined })
                  }
                />
              </Field>

              <Field label="Company Size (Min Employees)">
                <Input
                  type="number"
                  value={params.org_employee_count_range?.min ?? ''}
                  onChange={(e) =>
                    setParams({
                      ...params,
                      org_employee_count_range: {
                        ...params.org_employee_count_range,
                        min: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                />
              </Field>
            </div>

            <Field label="Job Titles">
              <TitleInput
                values={params.person_titles || []}
                onChange={(titles) => setParams({ ...params, person_titles: titles })}
              />
            </Field>

            <Field label="Seniority Levels">
              <SenioritySelect
                values={params.person_seniorities || []}
                onChange={(seniorities) =>
                  setParams({ ...params, person_seniorities: seniorities })
                }
              />
            </Field>

            <Field label="Email Status">
              <EmailStatusSelect
                values={params.contact_email_status || []}
                onChange={(status) =>
                  setParams({ ...params, contact_email_status: status })
                }
              />
            </Field>
          </>
        )}

        <div className="flex gap-2 border-t pt-4">
          <Button variant="secondary" loading={doSearch.pending} onClick={() => void doSearch.run()}>
            Preview Results
          </Button>
          <Button loading={doImport.pending} onClick={() => void doImport.run()}>Import Now</Button>
          <Button
            variant="secondary"
            onClick={() => setSavingAsImport(!savingAsImport)}
          >
            {savingAsImport ? 'Cancel' : 'Save as Recurring'}
          </Button>
        </div>

        {savingAsImport && (
          <div className="space-y-3 border-t pt-3">
            <Field label="Import Name">
              <Input
                type="text"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="e.g., Tech Directors at 100+ person companies"
              />
            </Field>
            <Field label="Description">
              <Textarea
                rows={3}
                value={importDescription}
                onChange={(e) => setImportDescription(e.target.value)}
              />
            </Field>
            <Field label="Sync Interval (hours)" hint="0 = manual only, 24 = daily, 168 = weekly">
              <Input
                type="number"
                min={0}
                max={720}
                value={syncInterval}
                onChange={(e) => setSyncInterval(parseInt(e.target.value) || 0)}
              />
            </Field>
            <Button loading={saveAsConfig.pending} onClick={() => void saveAsConfig.run()}>Save Config</Button>
          </div>
        )}

        {showPreview && preview && (
          <div className="space-y-2 border-t pt-4">
            <p className="font-medium">
              Found {preview.breadcrumbs?.total_count || preview.contacts.length} contacts
            </p>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Title</Th>
                    <Th>Company</Th>
                  </tr>
                </thead>
                <tbody>
                  {preview.contacts.slice(0, 10).map((contact) => (
                    <tr key={contact.id}>
                      <Td>{contact.first_name} {contact.last_name}</Td>
                      <Td>{contact.title}</Td>
                      <Td>{contact.organization?.name || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ApolloEditImportModal({
  import: imp,
  onClose,
  onSaved,
}: {
  import: ApolloListImport;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(imp.name);
  const [description, setDescription] = useState(imp.description);
  const [syncInterval, setSyncInterval] = useState(imp.sync_interval_hours);
  const [enabled, setEnabled] = useState(imp.enabled);

  const save = useAction(async () => {
    try {
      await api.put(`/api/apollo/imports/${imp.id}`, {
        name,
        description,
        sync_interval_hours: syncInterval,
        enabled,
      });
      onSaved();
    } catch (err) {
      alert((err as ApiError).message);
    }
  });

  const deleteImport = useAction(async () => {
    if (!confirm('Are you sure? This will not delete imported contacts, only the config.')) {
      return;
    }
    try {
      await api.del(`/api/apollo/imports/${imp.id}`);
      onSaved();
    } catch (err) {
      alert((err as ApiError).message);
    }
  });

  return (
    <Modal open title="Edit Apollo Import" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Name">
          <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Sync Interval (hours)" hint="0 = manual only">
          <Input
            type="number"
            min={0}
            max={720}
            value={syncInterval}
            onChange={(e) => setSyncInterval(parseInt(e.target.value) || 0)}
          />
        </Field>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-ink-300"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="text-sm">Enabled for scheduled syncs</span>
        </label>

        <div className="flex gap-2 border-t pt-4">
          <Button loading={save.pending} onClick={() => void save.run()}>Save Changes</Button>
          <Button
            variant="danger"
            loading={deleteImport.pending}
            onClick={() => void deleteImport.run()}
          >
            Delete Config
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TitleInput({
  values,
  onChange,
}: {
  values: string[];
  onChange: (titles: string[]) => void;
}) {
  const [input, setInput] = useState('');

  return (
    <div className="space-y-2">
      <div className="flex gap-1 flex-wrap">
        {values.map((title) => (
          <span
            key={title}
            className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-sm text-blue-900"
          >
            {title}
            <button
              onClick={() => onChange(values.filter((t) => t !== title))}
              className="font-bold hover:text-blue-700"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a title and press Enter"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim()) {
              onChange([...values, input.trim()]);
              setInput('');
            }
          }}
        />
        <select
          onChange={(e) => {
            if (e.target.value && !values.includes(e.target.value)) {
              onChange([...values, e.target.value]);
              e.target.value = '';
            }
          }}
          className="rounded border border-ink-200 px-2 py-1 text-sm"
        >
          <option value="">Suggestions</option>
          {TITLE_SUGGESTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SenioritySelect({
  values,
  onChange,
}: {
  values: string[];
  onChange: (seniorities: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {SENIORITY_SUGGESTIONS.map((option) => (
        <label key={option.value} className="flex items-center gap-1">
          <input
            type="checkbox"
            className="rounded border-ink-300"
            checked={values.includes(option.value)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...values, option.value]);
              } else {
                onChange(values.filter((v) => v !== option.value));
              }
            }}
          />
          <span className="text-sm">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function EmailStatusSelect({
  values,
  onChange,
}: {
  values: string[];
  onChange: (status: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {EMAIL_STATUS_OPTIONS.map((option) => (
        <label key={option.value} className="flex items-center gap-1">
          <input
            type="checkbox"
            className="rounded border-ink-300"
            checked={values.includes(option.value)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...values, option.value]);
              } else {
                onChange(values.filter((v) => v !== option.value));
              }
            }}
          />
          <span className="text-sm">{option.label}</span>
        </label>
      ))}
    </div>
  );
}
